import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  MultiFactorError,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { useAuth } from '../hooks/useAuth';

const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/user-not-found': 'Incorrect email or password.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
  'auth/code-expired': 'Verification code has expired. Please sign in again.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/captcha-check-failed': 'reCAPTCHA verification failed. Please try again.',
};

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    return FIREBASE_ERROR_MESSAGES[code] || `Authentication error (${code}). Please try again.`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
}

const RESEND_COOLDOWN = 30; // seconds

export function LoginPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  // Resend cooldown
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, navigate]);

  // Clean up reCAPTCHA + countdown on unmount
  useEffect(() => {
    return () => {
      clearVerifier();
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearVerifier() {
    if (recaptchaVerifierRef.current) {
      try { recaptchaVerifierRef.current.clear(); } catch { /* ignore */ }
      recaptchaVerifierRef.current = null;
    }
    // Remove the old container and create a fresh one — reCAPTCHA tracks
    // rendered state per DOM element and cannot be re-rendered into the same node.
    const old = document.getElementById('recaptcha-container');
    if (old && old.parentNode) {
      const fresh = document.createElement('div');
      fresh.id = 'recaptcha-container';
      old.parentNode.replaceChild(fresh, old);
    }
  }

  function startCooldown() {
    setResendCountdown(RESEND_COOLDOWN);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  /**
   * Send (or resend) the MFA SMS challenge.
   * Clears any existing RecaptchaVerifier, creates a fresh one, and calls verifyPhoneNumber.
   */
  const sendMfaSms = useCallback(async (resolver: MultiFactorResolver): Promise<boolean> => {
    clearVerifier();

    const container = document.getElementById('recaptcha-container');
    if (!container) {
      setError('Unable to initialise verification. Please refresh the page.');
      return false;
    }

    try {
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      recaptchaVerifierRef.current = verifier;

      const phoneProvider = new PhoneAuthProvider(auth);
      const hint = resolver.hints[0];
      const vid = await phoneProvider.verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        verifier,
      );
      setVerificationId(vid);
      startCooldown();
      return true;
    } catch (err: unknown) {
      console.error('[MFA] sendMfaSms failed:', err);
      clearVerifier();
      // Show the raw error message — not the generic fallback
      const code = err && typeof err === 'object' && 'code' in err ? (err as {code:string}).code : '';
      const msg = err instanceof Error ? err.message : String(err);
      setError(code ? `MFA failed (${code}): ${msg}` : `MFA failed: ${msg}`);
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // No MFA → onAuthStateChanged handles redirect
    } catch (err: unknown) {
      const errorCode = err && typeof err === 'object' && 'code' in err
        ? (err as { code: string }).code
        : null;

      if (errorCode === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(auth, err as MultiFactorError);
          setMfaResolver(resolver);

          if (!resolver.hints.length) {
            setError('No MFA methods enrolled. Please contact the practice.');
            setSubmitting(false);
            return;
          }

          const sent = await sendMfaSms(resolver);
          if (sent) {
            setMfaStep(true);
          }
          setSubmitting(false);
          return;
        } catch (mfaErr: unknown) {
          console.error('[MFA] outer catch:', mfaErr);
          clearVerifier();
          const msg = mfaErr instanceof Error ? mfaErr.message : String(mfaErr);
          setError(`MFA setup failed: ${msg}`);
          setSubmitting(false);
          return;
        }
      }

      // Non-MFA error — show actual code
      console.error('[LOGIN] error:', errorCode, err);
      const msg = err instanceof Error ? err.message : String(err);
      setError(errorCode ? `Login failed (${errorCode}): ${msg}` : `Login failed: ${msg}`);
      setSubmitting(false);
    }
  }, [email, password, sendMfaSms]);

  const handleSmsSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!mfaResolver || !verificationId) return;
    setError('');
    setSubmitting(true);

    try {
      const credential = PhoneAuthProvider.credential(verificationId, smsCode.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      await mfaResolver.resolveSignIn(assertion);
      // onAuthStateChanged handles redirect
    } catch (err) {
      // Clear verifier on failed OTP so retry is possible
      clearVerifier();
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [mfaResolver, verificationId, smsCode]);

  const handleResend = useCallback(async () => {
    if (!mfaResolver || resendCountdown > 0) return;
    setError('');
    setResendSuccess(false);
    setSubmitting(true);

    const sent = await sendMfaSms(mfaResolver);
    if (sent) {
      setResendSuccess(true);
      setSmsCode('');
      setTimeout(() => setResendSuccess(false), 3000);
    }
    setSubmitting(false);
  }, [mfaResolver, resendCountdown, sendMfaSms]);

  const handleBackToLogin = useCallback(() => {
    setMfaStep(false);
    setSmsCode('');
    setVerificationId('');
    setMfaResolver(null);
    setError('');
    setResendCountdown(0);
    setResendSuccess(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    clearVerifier();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      {/* Invisible reCAPTCHA anchor — must always be in DOM */}
      <div id="recaptcha-container" />

      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
            IKhan Gynae
          </h1>
          <p className="text-sm text-center text-gray-500 mb-8">
            Practice Management System
          </p>

          {error && (
            <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {!mfaStep ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="you@ikhangynae.co.za"
                  autoComplete="email"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoComplete="current-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSmsSubmit} className="space-y-4">
              <p className="text-sm text-gray-600">
                A verification code has been sent to your registered mobile number. Please enter it below.
              </p>
              <div>
                <label htmlFor="smsCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Code
                </label>
                <input
                  id="smsCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  autoFocus
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent tracking-widest text-center text-lg"
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
              </div>
              <button
                type="submit"
                disabled={submitting || smsCode.length < 6}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Verifying...' : 'Verify'}
              </button>

              {/* Resend code */}
              {resendSuccess && (
                <div className="p-2 rounded bg-green-50 border border-green-200 text-green-700 text-xs text-center">
                  Code resent successfully.
                </div>
              )}
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCountdown > 0 || submitting}
                className="w-full py-2 px-4 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resendCountdown > 0 ? `Resend code in ${resendCountdown}s` : 'Resend code'}
              </button>
              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full py-2 px-4 text-gray-500 text-sm hover:text-gray-900 transition-colors"
              >
                Back to sign in
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              New patient?{' '}
              <Link to="/register" className="text-blue-600 hover:text-blue-800 font-medium">
                Register online
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
